// The `money` subpath, not the package root: the root also exports the CSV
// parser, which imports `node:crypto` -- harmless on the server, but it drags a
// browser-externalised builtin into the bundle for code the web app never runs.
import { formatMinor, toMinor } from '@vmoney/shared/money'

/**
 * Display helpers.
 *
 * `formatMinor` comes from `@vmoney/shared` on purpose: TWD and JPY are
 * zero-decimal, so any client-side `/100` would silently divide the sample
 * statement's amounts by a hundred.
 */

export function money(minor: number, currency = 'TWD'): string {
  return formatMinor(minor, currency)
}

/** With a sign, for balances where the direction is the point. */
export function signedMoney(minor: number, currency = 'TWD'): string {
  const body = formatMinor(Math.abs(minor), currency)
  if (minor === 0) return body
  return `${minor > 0 ? '+' : '-'}${body}`
}

const WEEKDAYS = ['週日', '週一', '週二', '週三', '週四', '週五', '週六']

/**
 * Format an ISO date without going through `new Date(...)` local time.
 *
 * Dates are stored as the calendar day they happened on, with no timezone. A
 * `Date` in a UTC+8 browser renders `2026-09-15` as the 15th, but the same code
 * in a UTC-5 browser renders the 14th -- and the sort key says the 15th.
 */
export function shortDate(iso: string): string {
  const [, month, day] = iso.split('-')
  return `${Number(month)}/${Number(day)}`
}

export function longDate(iso: string): string {
  const [year, month, day] = iso.split('-')
  const weekday = WEEKDAYS[dayOfWeek(iso)]
  return `${year}/${month}/${day} ${weekday}`
}

function dayOfWeek(iso: string): number {
  return new Date(`${iso}T00:00:00Z`).getUTCDay()
}

/** `YYYY-MM` -> `2026年9月`, for headings. */
export function monthLabel(at: string): string {
  const [year, month] = at.split('-')
  return month ? `${year}年${Number(month)}月` : `${year}年`
}

export function todayIso(): string {
  // Local calendar day, not UTC: an expense entered at 8am in Taipei belongs to
  // today, and `toISOString()` would call it yesterday.
  const now = new Date()
  const y = now.getFullYear()
  const m = `${now.getMonth() + 1}`.padStart(2, '0')
  const d = `${now.getDate()}`.padStart(2, '0')
  return `${y}-${m}-${d}`
}

export function currentMonth(): string {
  return todayIso().slice(0, 7)
}

export function currentYear(): string {
  return todayIso().slice(0, 4)
}

/** Shift a `YYYY-MM` by whole months, or a `YYYY` by whole years. */
export function shiftPeriod(at: string, by: number): string {
  const parts = at.split('-')
  if (parts.length === 1) return String(Number(parts[0]) + by)
  const year = Number(parts[0])
  const month = Number(parts[1]) - 1 + by
  const shiftedYear = year + Math.floor(month / 12)
  const shiftedMonth = ((month % 12) + 12) % 12
  return `${shiftedYear}-${`${shiftedMonth + 1}`.padStart(2, '0')}`
}

/** `2026-09-15T08:30:00.000Z` -> `9/15 16:30` in the viewer's timezone. */
export function timestamp(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return `${d.getMonth() + 1}/${d.getDate()} ${`${d.getHours()}`.padStart(2, '0')}:${`${d.getMinutes()}`.padStart(2, '0')}`
}

/**
 * Parse a user-typed amount into minor units, or `undefined` if unusable.
 *
 * Delegates the arithmetic to `toMinor`, which rejects more precision than the
 * currency has rather than rounding it away -- so `101.5` TWD is refused here
 * instead of being quietly filed as 101 or 102.
 */
export function parseAmount(input: string, currency = 'TWD'): number | undefined {
  const text = input.trim().replace(/,/g, '')
  if (!/^\d+(\.\d+)?$/.test(text)) return undefined
  try {
    const minor = toMinor(text, currency)
    return minor > 0 ? minor : undefined
  } catch {
    return undefined
  }
}
